import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
    makeStyles,
    Button,
    Typography,
    CircularProgress,
} from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import logo from "../../assets/duo-lockup-horizontal-light.svg";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: theme.palette.type === "dark" ? "#121212" : "#f5f5f5",
        padding: theme.spacing(3),
    },
    card: {
        backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#ffffff",
        borderRadius: 16,
        padding: theme.spacing(5, 4),
        maxWidth: 480,
        width: "100%",
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    logo: {
        height: 40,
        width: "auto",
        marginBottom: theme.spacing(1),
        filter: theme.palette.type === "dark" ? "none" : "brightness(0.1)",
    },
    iconWrapper: {
        width: 72,
        height: 72,
        borderRadius: "50%",
        backgroundColor: "rgba(198,40,40,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(1),
    },
    lockIcon: {
        fontSize: 36,
        color: "#c62828",
    },
    title: {
        fontWeight: 700,
        color: "#c62828",
        fontSize: "1.4rem",
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: "0.95rem",
        lineHeight: 1.6,
    },
    dueDate: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    renewButton: {
        marginTop: theme.spacing(1),
        padding: "10px 32px",
        fontWeight: 700,
        fontSize: "0.95rem",
        borderRadius: 8,
        backgroundColor: "#c62828",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#b71c1c",
        },
    },
    checkingRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: theme.palette.text.disabled,
        fontSize: "0.78rem",
        marginTop: theme.spacing(1),
    },
    logoutButton: {
        color: theme.palette.text.secondary,
        fontSize: "0.8rem",
        marginTop: theme.spacing(0.5),
        textDecoration: "underline",
        cursor: "pointer",
        background: "none",
        border: "none",
        padding: 0,
    },
}));

const CHECK_INTERVAL_MS = 30000; // verifica a cada 30s

const Blocked = () => {
    const classes = useStyles();
    const history = useHistory();
    const { user, handleLogout } = useContext(AuthContext);
    const { finding } = useCompanies();
    const [dueDate, setDueDate] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkLicense = async () => {
        if (!user?.companyId) return;
        setChecking(true);
        try {
            const company = await finding(user.companyId);
            const due = company.dueDate ? moment(company.dueDate) : null;
            setDueDate(due);

            // Se a licença foi renovada, volta para o sistema
            if (due && due.diff(moment(), "days") >= 0) {
                history.push("/tickets");
            }
        } catch (err) {
            // silencioso — não exibir erro aqui
        } finally {
            setChecking(false);
        }
    };

    // Verifica ao montar
    useEffect(() => {
        checkLicense();
    }, [user?.companyId]);

    // Verifica periodicamente
    useEffect(() => {
        const interval = setInterval(checkLicense, CHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [user?.companyId]);

    return (
        <div className={classes.root}>
            <div className={classes.card}>
                <img src={logo} alt="logo" className={classes.logo} />

                <div className={classes.iconWrapper}>
                    <LockOutlinedIcon className={classes.lockIcon} />
                </div>

                <Typography className={classes.title}>
                    Acesso suspenso
                </Typography>

                <Typography className={classes.subtitle}>
                    A licença da sua empresa expirou
                    {dueDate && (
                        <> em <span className={classes.dueDate}>{dueDate.format("DD/MM/YYYY")}</span></>
                    )}.
                    <br />
                    Renove para restaurar o acesso completo ao sistema.
                </Typography>

                <Button
                    variant="contained"
                    className={classes.renewButton}
                    onClick={() => history.push("/financeiro")}
                    disableElevation
                >
                    Ir para Financeiro
                </Button>

                <div className={classes.checkingRow}>
                    {checking
                        ? <><CircularProgress size={12} /><span>Verificando licença...</span></>
                        : <><RefreshIcon style={{ fontSize: 14 }} /><span>Verificação automática a cada 30s</span></>
                    }
                </div>

                <button className={classes.logoutButton} onClick={handleLogout}>
                    Sair da conta
                </button>
            </div>
        </div>
    );
};

export default Blocked;
